/**
 * Schedule Repository
 */

import { BaseRepository } from './BaseRepository';
import { Schedule } from '../types/WorkflowEntities';

class ScheduleRepository extends BaseRepository<Schedule> {
    protected entityName = 'schedule';
    protected graphQLType = 'Schedule';

    // ✅ FERP SAFETY: Mandatory repository pattern method with tenant isolation
    async findByTenantAndId(tenantId: string, id: string): Promise<Schedule | null> {
        await this.validateRepositorySafety({
            operation: 'FIND_BY_TENANT_AND_ID',
            domain: 'OPERATIONAL',
            tenantId,
            entityType: 'Schedule',
            criticalityLevel: 'HIGH', // Schedules are critical for automation
            metadata: { id, scheduleRepository: true }
        });

        const query = `
            query GetScheduleByTenantAndId($id: String!, $tenant: String!) {
                schedule(id: $id, tenant: $tenant) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        try {
            const result = await this.query<{ schedule: Schedule | null }>(query, {
                id,
                tenant: tenantId
            }, {
                cache: true,
                cacheTime: 2 * 60 * 1000 // 2 minutes cache for schedules
            });

            const schedule = result.schedule || null;

            // ✅ FERP SAFETY: Log successful operation with schedule details
            if (this.auditTrail) {
                await this.auditTrail.logOperationSuccess({
                    operation: 'FIND_BY_TENANT_AND_ID',
                    tenantId,
                    entityType: 'Schedule',
                    entityId: id,
                    found: !!schedule,
                    scheduleEnabled: schedule?.enabled,
                    scheduleNextRun: schedule?.nextRun,
                    scheduleCron: schedule?.cronExpression,
                    timestamp: Date.now()
                });
            }

            return schedule;
        } catch (error) {
            // ✅ FERP SAFETY: Log failed operation
            if (this.auditTrail) {
                await this.auditTrail.logOperationFailure({
                    operation: 'FIND_BY_TENANT_AND_ID',
                    tenantId,
                    entityType: 'Schedule',
                    entityId: id,
                    error: error instanceof Error ? error.message : String(error),
                    timestamp: Date.now()
                });
            }
            throw error;
        }
    }

    // Legacy findById method - redirects to findByTenantAndId
    async findById(scheduleId: string): Promise<Schedule | null> {
        return this.findByTenantAndId(this.tenant, scheduleId);
    }

    // Schedule-specific methods
    async enable(scheduleId: string): Promise<any> {
        const mutation = `
            mutation EnableSchedule($scheduleId: ID!) {
                enableSchedule(scheduleId: $scheduleId) {
                    success
                    nextRun
                }
            }
        `;

        return this.executeQuery(mutation, { scheduleId });
    }

    async disable(scheduleId: string): Promise<any> {
        const mutation = `
            mutation DisableSchedule($scheduleId: ID!) {
                disableSchedule(scheduleId: $scheduleId) {
                    success
                }
            }
        `;

        return this.executeQuery(mutation, { scheduleId });
    }

    async trigger(scheduleId: string, payload?: any): Promise<any> {
        const mutation = `
            mutation TriggerSchedule($scheduleId: ID!, $payload: JSON) {
                triggerSchedule(scheduleId: $scheduleId, payload: $payload) {
                    success
                    executionId
                }
            }
        `;

        return this.executeQuery(mutation, { scheduleId, payload });
    }

    async getUpcoming(limit?: number): Promise<any[]> {
        const query = `
            query GetUpcomingSchedules($limit: Int) {
                upcomingSchedules(limit: $limit) {
                    scheduleId
                    name
                    nextRun
                    cronExpression
                }
            }
        `;

        const result = await this.executeQuery(query, { limit });
        return result.upcomingSchedules || [];
    }

    async getHistory(scheduleId: string, limit?: number): Promise<any[]> {
        const query = `
            query GetScheduleHistory($scheduleId: ID!, $limit: Int) {
                scheduleHistory(scheduleId: $scheduleId, limit: $limit) {
                    id
                    scheduledTime
                    executedTime
                    status
                    result
                    error
                }
            }
        `;

        const result = await this.executeQuery(query, { scheduleId, limit });
        return result.scheduleHistory || [];
    }

    async validateCron(cronExpression: string): Promise<any> {
        const query = `
            query ValidateCronExpression($cronExpression: String!) {
                validateCron(cronExpression: $cronExpression) {
                    valid
                    error
                    nextRuns
                    description
                }
            }
        `;

        return this.executeQuery(query, { cronExpression });
    }

    protected getFieldSelection(): string {
        return `
            id
            name
            description
            cronExpression
            timezone
            enabled
            nextRun
            lastRun
            runCount
            targetWorkflow
            payload
            createdAt
            updatedAt
        `;
    }
}

export const scheduleRepository = new ScheduleRepository();
export { ScheduleRepository };